import { useState } from 'react';
import { useStore } from '@/relatos/store';
import { hasNip07 } from '@/relatos/client';

/** Key-loss warning is mandatory copy — CLAUDE.md §4 (informed loss, never surprise loss). */
function KeyLossWarning() {
  return (
    <div className="rl-alert" role="note">
      <strong>Key loss is permanent.</strong> There is no password reset, no recovery phrase
      escrow, no admin override. If you lose your secret key you lose this identity and every
      board membership with it. Download the backup and keep it somewhere safe.
    </div>
  );
}

export function LoginPanel() {
  const { loginWithNip07, loginWithNsec, createIdentity, unlockWithPassphrase, ready, locked } = useStore();
  const [nsec, setNsec] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [unlockPw, setUnlockPw] = useState('');
  const [generated, setGenerated] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!ready) return <div className="rl-panel rl-empty">Warming up…</div>;

  if (locked) {
    const unlock = () => {
      setErr(null);
      try { unlockWithPassphrase(unlockPw); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    };
    return (
      <div>
        <div className="rl-cat">Unlock your key</div>
        <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
          <p className="rl-small">Your secret key is stored encrypted (NIP-49). Enter your passphrase to unlock this device.</p>
          <div className="rl-field">
            <label htmlFor="unlock-pw">Passphrase</label>
            <input id="unlock-pw" className="rl-input" type="password" value={unlockPw}
                   onChange={e => setUnlockPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && unlock()} autoComplete="current-password" />
          </div>
          <button className="rl-btn rl-btn-primary" onClick={unlock} disabled={!unlockPw}>Unlock</button>
          {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
        </div>
      </div>
    );
  }

  const doNip07 = async () => {
    setBusy(true); setErr(null);
    try { await loginWithNip07(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };
  const doNsec = () => {
    setErr(null);
    if (pw.length < 8) { setErr('Passphrase must be at least 8 characters — it encrypts your key on this device.'); return; }
    try { loginWithNsec(nsec, pw); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const doGenerate = () => {
    setErr(null);
    if (pw2.length < 8) { setErr('Pick a passphrase of at least 8 characters first.'); return; }
    const { nsec } = createIdentity(pw2);
    setGenerated(nsec);
  };
  const download = () => {
    if (!generated) return;
    const blob = new Blob([generated], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'relatos-key-backup.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div className="rl-cat">Sign in</div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        <KeyLossWarning />

        <div className="rl-h2">Browser extension (NIP-07)</div>
        {hasNip07() ? (
          <button className="rl-btn rl-btn-primary" onClick={doNip07} disabled={busy}>
            {busy ? 'Connecting…' : 'Connect extension'}
          </button>
        ) : (
          <p className="rl-small" style={{ margin: '0 0 6px' }}>
            No NIP-07 extension detected (Alby, nos2x, …). Use a key below instead.
          </p>
        )}

        <hr className="rl-hr" />
        <div className="rl-h2">Use an existing key</div>
        <div className="rl-field">
          <label htmlFor="nsec-in">Secret key (nsec…)</label>
          <input
            id="nsec-in" className="rl-input rl-mono" type="password"
            placeholder="nsec1…" value={nsec}
            onChange={e => setNsec(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="rl-field">
          <label htmlFor="nsec-pw">Passphrase (encrypts the key on this device)</label>
          <input id="nsec-pw" className="rl-input" type="password" value={pw}
                 onChange={e => setPw(e.target.value)} autoComplete="new-password" />
        </div>
        <button className="rl-btn" onClick={doNsec} disabled={!nsec.trim() || pw.length < 8}>Sign in with key</button>

        <hr className="rl-hr" />
        <div className="rl-h2">New identity</div>
        {!generated ? (
          <>
            <div className="rl-field">
              <label htmlFor="gen-pw">Passphrase (encrypts the key on this device)</label>
              <input id="gen-pw" className="rl-input" type="password" value={pw2}
                     onChange={e => setPw2(e.target.value)} autoComplete="new-password" />
            </div>
            <button className="rl-btn" onClick={doGenerate} disabled={pw2.length < 8}>Generate a fresh key</button>
          </>
        ) : (
          <div>
            <p className="rl-small">This is the only time your secret key is shown. Back it up now:</p>
            <div className="rl-invite-out">{generated}</div>
            <div className="rl-actions" style={{ marginTop: 8 }}>
              <button className="rl-btn rl-btn-primary" onClick={download}>Download backup</button>
              <button className="rl-btn rl-btn-sm" onClick={() => navigator.clipboard.writeText(generated)}>Copy</button>
            </div>
          </div>
        )}

        {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
      </div>
    </div>
  );
}
